import Big from 'big.js'
import { Combobox, CustomTableRef, EInputTypes, GetItemsProps, InputColumnTableProps, InputTable, InputTextCustom } from 'medere-ui-framework'
import React, { BaseSyntheticEvent, forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { getAllAccountingAccountsByLevel } from '../../servicesHooks/Accounting/useAccountingService'
import { AccountingAccount } from '../../interface/interface'
import { ItemVaucher, OrderInfo, TaxItemVaucher } from '../../interface/orderOfProvision'
import { useTaxesPaid } from '../../components/context/TaxesPaidContext'
import { useTaxService } from '../../servicesHooks/Tax/useTaxService'

const MAX_DECIMAL = 2

interface OrderOfProvisionItemProps {
    items: ItemVaucher[]
    accounting: any
    defaultAccountingAccount: AccountingAccount | null
    itemTaxesRef: React.MutableRefObject<any>
    selectedSupplier: any
    deleteVaucherItem: (item: ItemVaucher) => void
    addItemToArray: (item: ItemVaucher) => void
    addItemValidation: (onSuccess: (data: OrderInfo) => void, _errors?: Partial<Record<keyof OrderInfo, boolean>> | undefined, ignoredFields?: (keyof OrderInfo)[] | undefined) => {
        invalidFields: (keyof OrderInfo)[];
        onSuccessResult: void | null;
    }
    administrativeForm: number | null
    supplier: any
}

export interface OrderOfProvisionItemsRef {
    refreshItems: () => void
}

const OrderOfProvisionItems = forwardRef((props: OrderOfProvisionItemProps, ref: Ref<OrderOfProvisionItemsRef>) => {
    const { getAllTaxTypes, getAllTaxTypesCombobox } = useTaxService();
    const { defaultAccountingAccount, accounting, selectedSupplier, items, deleteVaucherItem, addItemToArray, addItemValidation, administrativeForm, supplier } = props
    const intl = useIntl()
    const orderRef = useRef<CustomTableRef>(null)
    const [errorItem, setErrorItem] = useState({errorDescription: false, errorAccount: false, errorAmount: false, errorTax: false, message: 'Debe completar este campo'})

    const {updatePaidTax, updateTotalWithoutTaxes, updateExemptTaxAmount, updateVatNotTaxedAmount} = useTaxesPaid();
    const [itemToVaucher, setItemToVaucher] = useState<ItemVaucher>({
        id: "",
        description: '',
        account: null,
        amount: Big(0),
        tax: null,
        taxAmount: Big(0),
        total: Big(0)
    })

    function cleanItemVaucher() {
        setItemToVaucher({
            id: "",
            description: '',
            account: null,
            amount: Big(0),
            tax: null,
            taxAmount: Big(0),
            total: Big(0)
        })
    }

    function validateItem() {
        let isValid = true;
        const newErrorItem = {
            errorDescription: false,
            errorAccount: false,
            errorAmount: false,
            errorTax: false,
            message: 'Debe completar este campo',
        };
    
        if (itemToVaucher?.description?.trim() === '') {
            newErrorItem.errorDescription = true;
            isValid = false;
        }
        if (!itemToVaucher?.account && !defaultAccountingAccount) {
            newErrorItem.errorAccount = true;
            isValid = false;
        }
        if (Number(itemToVaucher?.amount) <= 0) {
            newErrorItem.errorAmount = true;
            isValid = false;
        }
        if (!itemToVaucher?.tax) {
            newErrorItem.errorTax = true;
            isValid = false;
        }
    
        setErrorItem(newErrorItem);
        return isValid;
    }

    useEffect(() => {
        const ivaPrice = itemToVaucher.amount.times(itemToVaucher.tax?.taxvalue || 0).div(100)
        const calculatedIvaPrice = isNaN(Number(ivaPrice.toString())) ? Big(0) : ivaPrice

        setItemToVaucher(prev => ({
            ...prev,
            taxAmount: calculatedIvaPrice
        }))
        if (!itemToVaucher.amount && itemToVaucher.taxAmount) {
            setItemToVaucher(prev => ({
                ...prev,
                total: Big(0)
            }))
        } else {
            const total = itemToVaucher.amount.plus(taxValue)
            setItemToVaucher(prev => ({
                ...prev,
                total
            }))
        }

    }, [itemToVaucher.amount, itemToVaucher.tax])

    const taxValue = useMemo(() => {
        if (itemToVaucher.tax?.taxvalue == null) return Big(0)
        const _taxValue = itemToVaucher.amount.times(itemToVaucher.tax?.taxvalue / 100)
        return isNaN(Number(_taxValue)) ? Big(0) : _taxValue
    }, [itemToVaucher.amount, itemToVaucher.tax])

    useImperativeHandle(ref, () => ({
        refreshItems: () => {
            if (orderRef.current && orderRef.current.refreshTable) orderRef.current?.refreshTable()
        }
    }), [])

    const arrayColumns: InputColumnTableProps<ItemVaucher>[] = [{
        header: 'Descripción',
        input: () => {
            return <InputTextCustom
                value={itemToVaucher.description}
                onChange={(e) => {
                    setErrorItem({ ...errorItem, errorDescription: false})
                    setItemToVaucher(prev => ({ ...prev, description: e.target.value }))
                }}
                disable={!supplier}
                error={errorItem.errorDescription}
                caption={errorItem.errorDescription ? errorItem.message : ''}
            />
        },
        className: 'min-w-200 flex-grow',
        body: (e) => <p>{e.description}</p>
    }, {
        header: 'Cuenta',
        input: () => {
            return <Combobox
                getItems={getAllAccountingAccountsByLevel}
                params={{
                    level: accounting?.treesize,
                    accounting: accounting?.accounting,
                    administrativeForm: administrativeForm,
                }} optionLabel='fulldescription'
                setValue={(e: any) => {
                    setErrorItem({ ...errorItem, errorAccount: false})
                    setItemToVaucher(prev => ({
                        ...prev,
                        account: e
                    }))
                }}
                value={itemToVaucher.account ?? defaultAccountingAccount}
                disable={!supplier}
                error={errorItem.errorAccount}
                caption={errorItem.errorAccount ? errorItem.message : ''}
            />
        },
        className: 'min-w-180 w-full max-w-250',
        body: (e) => <p className='text-nowrap text-ellipsis'>{e.account?.fulldescription ?? e.account?.description}</p>,
    }, {
        header: 'Importe',
        input: () => {
            return <InputTextCustom
                value={Number(itemToVaucher.amount)}
                onChange={(e: BaseSyntheticEvent) => {
                    setErrorItem({ ...errorItem, errorAmount: false})
                    if (e.target.value === '' || e.target.value == null) return setItemToVaucher(prev => ({ ...prev, amount: Big(0) }))
                    setItemToVaucher(prev => ({
                        ...prev,
                        amount: Big(e.target.value)
                    })
                    )
                }}
                inputType={EInputTypes.NUMBER}
                disable={!supplier}
                error={errorItem.errorAmount}
                caption={errorItem.errorAmount ? 'Valor inválido' : ''}
                maxFractionDigits={5}
            />
        },
        className: 'min-w-120 max-w-130',
        body: (e) => <p>$ {new Intl.NumberFormat('es-ES').format(Number(Big(e.amount).toFixed(MAX_DECIMAL)))}</p>,
    }, {
        header: '% iva',
        input: () => {
            return <Combobox
                getItems={(getItemProps: GetItemsProps) => {
                    return getAllTaxTypesCombobox(getItemProps)
                        .then(resp => resp)
                }}
                optionLabel='taxname'
                params={{isVatTax: true }}
                value={itemToVaucher.tax}
                setValue={(e: TaxItemVaucher) => {
                    setErrorItem({ ...errorItem, errorTax: false})
                    setItemToVaucher(prev => ({
                        ...prev,
                        tax: e
                    }))
                }}
                disable={!supplier}
                error={errorItem.errorTax}
                caption={errorItem.errorTax ? 'Debe completar IVA' : ''}
            />
        },
        className: 'min-w-120 w-full max-w-150',
        body: (e) => <p>{e.tax?.taxname}</p>
    }, {
        header: '$ iva',
        input: () => {
            return <p >$ {Big(taxValue).toFixed(MAX_DECIMAL)}</p>
        },
        className: 'min-w-180 align-items-center',
        body: (e) => <p>$ {new Intl.NumberFormat('es-ES').format(Number(Big(e.taxAmount).toFixed(MAX_DECIMAL)))}</p>,
    }, {
        header: 'Total',
        input: () => {
            return <p >$ {Big(itemToVaucher.total).toFixed(MAX_DECIMAL)}</p>
        },
        className: 'min-w-180 max-w-200',
        body: (e) => <p>$ {new Intl.NumberFormat('es-ES').format(Number(Big(e.total).toFixed(MAX_DECIMAL)))}</p>,
    }]

    return (
        <>
            <h2 className='title font-semibold'>Items</h2>
            <div>
                <InputTable
                    items={items}
                    columns={arrayColumns}
                    onAdd={() => {
                        if (validateItem()) {
                            addItemToArray(itemToVaucher)
                            cleanItemVaucher()
                            updatePaidTax(itemToVaucher.tax.taxtype, itemToVaucher.tax.taxaccount, itemToVaucher.tax.taxname, Big(itemToVaucher.taxAmount), itemToVaucher.tax.taxaccountdesc, true)
                            /*if (itemToVaucher.tax.exemptvattax) updateExemptTaxAmount(Big(itemToVaucher.amount)) //no deberían hacer nada porque no aparecen el el combobox de la tabla de items
                            else if (itemToVaucher.tax.vatnottaxed) updateVatNotTaxedAmount(Big(itemToVaucher.amount))
                            else*/ updateTotalWithoutTaxes(Big(itemToVaucher.amount))
                        }
                    }}
                    onDelete={(e) => { 
                        updatePaidTax(e.tax.taxtype, e.tax.taxaccount, e.tax.taxname, Big(e.taxAmount.mul(-1)), e.tax.taxaccountdesc, true)
                        /*if (e.tax.exemptvattax) updateExemptTaxAmount(Big(e.amount).mul(Big(-1))) //no deberían hacer nada porque no aparecen el el combobox de la tabla de items
                        else if (e.tax.vatnottaxed) updateVatNotTaxedAmount(Big(e.amount).mul(Big(-1)))
                        else*/ updateTotalWithoutTaxes(Big(e.amount).mul(Big(-1)))
                        deleteVaucherItem(e);
                    }}
                    headerDivClassName='oop-input-table-header gap-3 min-w-1300 px-3'
                    inputDivClassName='gap-3 bg-white min-w-1300 border-1 border-soft-color px-3 align-items-center'
                    itemsClassName='gap-3 min-w-1300 bg-white border-1 border-soft-color px-3 align-items-center'
                    labelAddBtn='Agregar'
                    divBtnClassName='max-w-120 min-w-120 flex justify-content-center align-items-center'
                    className='overflow-x-auto'
                />
            </div>
        </>
    )
})

export default OrderOfProvisionItems
